(ns nosco.ui.html
  "Utilities and components to render user-provided HTML in React, and prevent
     XSS avenues.

     You should never ever use dangerouslySetInnerHTML any more. Instead, pick
     one of the components in here, therefore signalling the intention to the
     code reader."
  (:require ["dompurify" :as dompurify]
            ["linkify-html" :as linkify-html]
            [clojure.string :as string]
            [hx.react :refer [defnc]]
            [nosco.react]))
;;; React doesn't actually execute <script> tags (see https://stackoverflow.com/questions/64201229/script-execution-with-dangerouslysetinnerhtml)
;; In order to actually test this, a proper XSS vector is this:
;; <img src onerror= "var script = document.createElement('script');script.innerText = 'alert(document.title)';document.body.appendChild(script);" >
;; <p>nothing to see here</p>
;; which is actually cleaned up by dompurify as it has a whitelist of allowed attributes

;; In principle we could keep the purify options in-sync with the Prosemirror schema
;; so that we remove any attributes that we don't expect
;; but let's keep it more open to give power users (us) a little
;; bit of power...

(defn- find-empty-paragraphs [dom]
  (let [target-brs (.querySelectorAll dom "br[data-origin=\"nosco-pm-empty-fix\"]")
        target-ps (->> target-brs
                       (map #(.closest % "p"))
                       (remove nil?)
                       distinct)]
    target-ps))

(defn sanitize-html-editor
  "Sanitise an html string that was produced by a rich text editor."
  [html-string]
  (let [dom (dompurify/sanitize
             html-string
             #js {:USE_PROFILES #js {:html true}
                  :ADD_TAGS #js ["iframe"]
                  :ADD_ATTR #js ["nr-ignore" "target" "allow"
                                 "sandbox" "allowfullscreen"]
                  :RETURN_DOM true})
        target-ps (find-empty-paragraphs dom)]
    (doseq [el target-ps]
      (.. el -classList (add "nosco-pm-empty")))
    (.-innerHTML dom)))

(defn sanitize-html-strict
  "Sanitise an html string in the strictest way possible."
  [html-string]
  (dompurify/sanitize html-string #js {:USE_PROFILES #js {:html true}
                                       :ADD_ATTR #js ["nr-ignore" "target"]}))

(defn- node-with-only-br? [node]
  (let [child-nodes (.-children node)]
    (and
     (string/blank? (.-textContent node))
     (= 1 (.-length child-nodes))
     (= "BR" (-> child-nodes first .-tagName)))))

(defn node-with-eventually-only-br? [node]
  (let [child-nodes (.-children node)]
    (or (node-with-only-br? node)
        (and (= 1 (.-length child-nodes))
             (string/blank? (.-textContent (-> child-nodes first)))
             ;; sometimes Redactor would insert the <br> nested inside
             ;; a <strong> element, so try and figure this out.
             (#{"STRONG" "EM" "I" "B"} (-> child-nodes first .-tagName))
             (node-with-only-br? (-> child-nodes first))))))

(defn html-empty?
  "Checks to see if the provided HTML string has no content.

  Content means:
  - non-empty nodes with text
  - img nodes with non-empty src attributes

  It only checks at the top-level. Any nested non-empty elements are ignored."
  [html]
  (let [doc (.parseFromString (js/DOMParser.) html "text/html")
        children (.. doc -body -children)
        children (.from js/Array children)
        non-empty-el? #(and (not-empty (.-innerHTML %))
                            (not (node-with-eventually-only-br? %)))
        non-empty-img? #(and (= (.-tagName %) "IMG")
                             (not-empty (.getAttribute % "src")))
        non-empty-child (.find children #(or (non-empty-el? %)
                                             (non-empty-img? %)))]
    (not non-empty-child)))

(defn html->text [html]
  (let [doc (.parseFromString (js/DOMParser.) html "text/html")]
    (.. doc -body -textContent)))

(defn html-text-blank?
  "Check to see if some html string has any text content. Non-text elements are treated as empty.
   Non-html strings are treated as non-empty, in contrast with `html-empty?`"
  [html]
  (string/blank? (html->text html)))

(defn parse-html [html]
  (let [fragment
        (.parseFromString
         (js/DOMParser.)
         html
         "text/html")]
    (-> fragment .-body)))

(defn html= [html other-html]
  (.isEqualNode (parse-html html) (parse-html other-html)))

(defn html-lenient=
  "Compare two html texts, using only the text content and collapsing whitespace."
  [html other-html]
  (let [t1 (html->text html)
        t2 (html->text other-html)]
    (= (string/replace t1 #"\s+" " ")
       (string/replace t2 #"\s+" " "))))

(defn equal-nodes? [html1 html2]
  (try
    (.isEqualNode (parse-html html1) (parse-html html2))
    (catch :default _
      false)))

(defn lazy-loading [html]
  (let [imgs (.querySelectorAll html "img")
        iframes (.querySelectorAll html "iframe")]
    (doseq [el (concat (js/Array.from imgs) (js/Array.from iframes))]
      (.setAttribute el "loading" "lazy")))
  html)

(defn split-html-figures [html-str]
  (let [html (parse-html html-str)
        figures (.querySelectorAll html "figure")
        _ (doseq [f figures]
            (let [parent (.-parentNode f)]
              (.removeChild parent f)))]
    [html (js/Array.from figures)]))

(defn html->str [html]
  (.-outerHTML html))

(comment
  (html-empty? "<p></p>") ;; => true
  (html-empty? "foo bar") ;; => true
  (html-empty? "<p>foo bar</p>") ;; => false
  (html-text-blank? "<p></p>") ;; => true
  (html-text-blank? "foo bar") ;; => false
  (html-text-blank? "<p>foo bar</p>") ;; => false
  (html-text-blank? "<p><br></p>") ;; => true
  )
(defnc SafeHTMLStrict
  "Render user-provided html safely. Most strict of the components in this
   namespace."
  [{:keys [html tag] :or {tag :div} :as props}]
  [tag (nosco.react/merge-props
        (dissoc props :html :tag)
        {:dangerouslySetInnerHTML #js {:__html (sanitize-html-strict html)}})])

(defnc SafeHTMLEditor
  "Render user-provided HTML safely. Allows for editor features like e.g. iframes.
   Class defaults to nosco-redactor-text and is replaced if provided."
  [{:keys [html variant tag class extra-class nice-width linkify] :as props
    :or {variant :normal tag :div}}]
  (let [class (or class
                  ["nosco-redactor-text"
                   (when nice-width "nice-line-width")
                   (str "nosco-redactor-text--"
                        (case variant
                          :normal "normal"
                          :normal-short "normal-short"
                          :document "document"
                          :document-compact "document-compact"
                          :large "large"
                          :small "small"))])]
    [tag (nosco.react/merge-props
          (dissoc props :html :variant :tag :class :extra-class :nice-width :linkify)
          {:class (nosco.react/merge-class class extra-class)
           :dangerouslySetInnerHTML #js {:__html (cond-> html
                                                   linkify (linkify-html #js {:rel "noopener"
                                                                              :target "_blank"
                                                                              :truncate "40"})
                                                   :always (sanitize-html-editor))}})]))

(defnc UnsafeHTML
  "Create a tag passing inner html as-is. Handle with care! Never with
   user-provided input."
  [{:keys [html tag] :or {tag :div} :as props}]
  [tag (nosco.react/merge-props
        (dissoc props :html :tag)
        {:dangerouslySetInnerHTML #js {:__html html}})])
